import { z } from 'zod';

export const CreateYourOwnTemplateId = 'create-your-own';
export const YourCustomTemplateId = 'your-custom-template';

export const AlertTemplateIdSchema = z.enum([
  CreateYourOwnTemplateId,
  'new-product-launch',
  'new-product-top-10',
  'sales-cross-threshold',
  'sales-drop-threshold',
  'brand-enters-top-10-sales',
  'market-share-threshold',
  'market-share-change',
  'new-keyword',
  'brand-top-10-traffic',
  'emerging-keyword-trend',
  YourCustomTemplateId
]);

export const BeaconAlertTemplateIds = z.enum(
  AlertTemplateIdSchema.options.filter(
    (id) => id !== 'market-share-threshold' && id !== 'market-share-change'
  ) as [string, ...string[]]
);

export const AlertTemplateTagSchema = z.enum([
  'New Product',
  'Sales Threshold',
  'Share Movement',
  'Keyword',
  'Traffic',
  'Sales',
  'Promotions'
]);

export const AlertTemplateSchema = z.object({
  id: AlertTemplateIdSchema,
  valid: z.boolean(),
  title: z.string(),
  description: z.string(),
  tags: z.array(AlertTemplateTagSchema),
  types: z.array(z.string())
});

export type AlertTemplateIdType = z.infer<typeof AlertTemplateIdSchema>;
export type AtlasAlertTemplateIdType = z.infer<typeof AlertTemplateIdSchema>;
export type BeaconAlertTemplateIdType = z.infer<typeof BeaconAlertTemplateIds>;
export type AlertTemplateType = z.infer<typeof AlertTemplateSchema>;

export const AllAlertTemplates: AlertTemplateType[] = [
  {
    id: 'create-your-own',
    valid: true,
    title: 'Create Your Own Alert',
    description: 'Build custom alerts with any metric. Our AI makes set up fast and easy.',
    tags: ['Sales', 'Traffic', 'Promotions'],
    types: ['Choose from 8 alert types']
  },
  {
    id: 'new-product-launch',
    valid: true,
    title: 'New Product Launch',
    description: 'New product in the category has sales greater than $10,000 for the first time.',
    tags: ['New Product'],
    types: ['Retailer', 'Category', 'Product']
  },
  {
    id: 'new-product-top-10',
    valid: true,
    title: 'New Product Enters Top 10',
    description: 'New product in the category appears in top 10 sales for the first time.',
    tags: ['New Product'],
    types: ['Retailer', 'Category', 'Product']
  },
  {
    id: 'sales-cross-threshold',
    valid: true,
    title: 'Sales Cross Threshold',
    description: 'Sales for one brand are greater than sales for another in the last 4 weeks.',
    tags: ['Sales Threshold'],
    types: ['Retailer', 'Brand']
  },
  {
    id: 'sales-drop-threshold',
    valid: true,
    title: 'Sales Drop Threshold',
    description: 'Sales for one brand dropped more than 15% vs an average over the last 4 weeks.',
    tags: ['Sales Threshold'],
    types: ['Retailer', 'Brand']
  },
  {
    id: 'brand-enters-top-10-sales',
    valid: true,
    title: 'Brand Enters Top 10 Sales',
    description: 'A brand enters the top 10 in sales or market share for the first time in the last week.',
    tags: ['Share Movement'],
    types: ['Retailer', 'Brand']
  },
  {
    id: 'market-share-threshold',
    valid: true,
    title: 'Market Share Threshold',
    description: 'Any brand goes above 15% market share in a specific category in the last week.',
    tags: ['Share Movement'],
    types: ['Retailer', 'Category', 'Brand']
  },
  {
    id: 'market-share-change',
    valid: true,
    title: 'Market Share Change',
    description: 'Any brand with a market share change greater than 2% in either direction week over week.',
    tags: ['Share Movement'],
    types: ['Retailer', 'Category', 'Brand']
  },
  {
    id: 'new-keyword',
    valid: true,
    title: 'New Keyword',
    description: 'A new keyword has traffic in the last week for first time.',
    tags: ['Keyword'],
    types: ['Retailer', 'Brand', 'Keyword']
  },
  {
    id: 'brand-top-10-traffic',
    valid: true,
    title: 'Brand Enters Top 10 Traffic',
    description: 'A brand enters the top 10 paid share of voice for a specific keyword in the last week.',
    tags: ['Traffic'],
    types: ['Retailer', 'Brand', 'Keyword']
  },
  {
    id: 'emerging-keyword-trend',
    valid: true,
    title: 'Emerging Keyword Trend',
    description: 'An emerging keyword started trending with traffic growth greater than 50% week over week.',
    tags: ['Keyword'],
    types: ['Retailer', 'Keyword']
  }
];

export const AtlasAlertTemplates: AlertTemplateType[] = AllAlertTemplates;
export const BeaconAlertTemplates: AlertTemplateType[] = AllAlertTemplates.filter(
  (template) => template.id !== 'market-share-threshold' && template.id !== 'market-share-change'
);
